// forgot-handoff.js — the login screen -> forgot-password screen handoff
// (Brief 200).
//
// iOS hands the forgot-password screen two values directly, as properties on
// the view controller it is about to present:
//
//     vc.username  = _userNameTF.text;
//     vc.has_phone = has_phone;          // KFELoginViewController.m:297-298
//
// The web has a URL between the two screens instead of a presentation, so the
// pair is split:
//
//   * `username` travels in `?username=` (Brief 200 scope item 1), where it
//     survives a reload and keeps the screen from dead-ending on a direct
//     visit;
//   * `has_phone` travels through here, and deliberately NOT in the URL. It is
//     the server's answer about the account, and in a query string it would be
//     hand-editable into offering an SMS option to an account with no phone —
//     which would only earn a server error, but would be the web inventing a
//     state iOS cannot reach.
//
// ONE-SHOT, and keyed to the username it was armed for. A stale `has_phone`
// from an earlier attempt with a different username must never be believed, so
// `take…` clears the slot and the reader checks the name matches. When it does
// not, kfe-forgot-password re-runs check-username itself rather than guessing.
//
// sessionStorage, not localStorage — the same reasoning as Brief 194's expiry
// notice: this belongs to THIS tab's journey between two screens and must not
// resurface in a tab opened tomorrow.

/** @typedef {{ username: string, hasPhone: boolean }} ForgotHandoff */

export const FORGOT_HANDOFF_KEY = "kfe_forgot_handoff";

/**
 * Arm the handoff immediately before navigating to /forgot-password.
 * @param {ForgotHandoff} handoff
 */
export function setForgotHandoff({ username, hasPhone }) {
  try {
    sessionStorage.setItem(FORGOT_HANDOFF_KEY, JSON.stringify({ username, hasPhone: hasPhone === true }));
  } catch { /* private mode etc. — the next screen re-runs check-username */ }
}

/**
 * Read-and-clear. Returns null when nothing was armed, when the slot cannot be
 * read, or when its contents are not the shape we wrote.
 * @returns {ForgotHandoff | null}
 */
export function takeForgotHandoff() {
  let raw = null;
  try {
    raw = sessionStorage.getItem(FORGOT_HANDOFF_KEY);
    sessionStorage.removeItem(FORGOT_HANDOFF_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed?.username !== "string" || typeof parsed?.hasPhone !== "boolean") return null;
    return { username: parsed.username, hasPhone: parsed.hasPhone };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// …and back again
// ---------------------------------------------------------------------------

/**
 * The username to put back in the login screen's box when the learner returns
 * from the forgot-password flow — Brief 200 criterion 4's last clause, "back
 * to /login with the username still filled in".
 *
 * ON IOS THIS NEEDS NO MECHANISM AT ALL, which is why it is easy to miss.
 * The forgot-password screen is PRESENTED MODALLY over the login screen
 * (`presentViewController:` / `dismiss(animated:)`), so the login view
 * controller is never torn down and `_userNameTF.text` is simply still there
 * when the modal goes away. The web navigates instead, which unmounts
 * <kfe-login> and takes the typed text with it — so the text has to be
 * carried, and this is the slot that carries it.
 *
 * It is NOT the last-username store (auth-store's getLastUsername). That one
 * is iOS's `getLastUserName`: the last username to sign in SUCCESSFULLY,
 * written at login success and kept for good. A learner in this flow has not
 * signed in — that is the whole problem they are solving — so on a device
 * where nobody has ever logged in, the last-username store is empty and
 * cannot restore anything. The two look interchangeable and are not; a walk
 * test from a cold boot is what tells them apart.
 *
 * One-shot, for the same reason the handoff above is: it describes one trip
 * between two screens, and a username left lying here would pre-fill the box
 * for whoever opens the tab next.
 */
export const RETURNING_USERNAME_KEY = "kfe_forgot_returning_username";

/**
 * Called immediately before navigating back to /login.
 * @param {string} username
 */
export function setReturningUsername(username) {
  const trimmed = (username ?? "").trim();
  if (!trimmed) return;
  try { sessionStorage.setItem(RETURNING_USERNAME_KEY, trimmed); }
  catch { /* private mode etc. — the learner retypes it */ }
}

/**
 * Read-and-clear, on the login screen's mount.
 * @returns {string} "" when there is nothing to restore.
 */
export function takeReturningUsername() {
  try {
    const raw = sessionStorage.getItem(RETURNING_USERNAME_KEY);
    sessionStorage.removeItem(RETURNING_USERNAME_KEY);
    return raw ?? "";
  } catch {
    return "";
  }
}
